import { cn } from "@core/utils/cn";
import { forwardRef, type ComponentProps, type ReactNode } from "react";
import Logo from "../Logo";

type HeaderProps = ComponentProps<"header"> & {
  /** Optional content rendered absolutely centered (tabs, breadcrumbs, etc.) */
  center?: ReactNode;
  /** Optional content rendered on the left, before the logo (e.g. sidebar toggle) */
  leading?: ReactNode;
};

const Header = forwardRef<HTMLElement, HeaderProps>(
  ({ className, children, center, leading, ...props }, ref) => {
    return (
      <header
        ref={ref}
        className={cn(
          "sticky top-0 z-50 flex items-center justify-between",
          "h-[var(--app-header-h)] min-h-[var(--app-header-h)] max-h-[var(--app-header-h)]",
          "px-8 bg-white border-b border-neutral-200 overflow-hidden",
          className,
        )}
        {...props}
      >
        {/* Left — optional leading slot + Logo (always) */}
        <div className="flex items-center gap-3 shrink-0">
          {leading}
          <Logo />
        </div>

        {/* Center — absolute so it never pushes left/right content */}
        {center && (
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center gap-1">
            {center}
          </div>
        )}

        {/* Right — fully dynamic */}
        {children && (
          <div className="flex items-center gap-3 shrink-0">{children}</div>
        )}
      </header>
    );
  },
);

Header.displayName = "Header";

export default Header;
